<template>
  <div>
    <v-slide-y-transition leave-absolute v-if="!!error">
      <v-card :loading="loading">
        <error-handler v-if="!!error" :error="error" />
      </v-card>
    </v-slide-y-transition>
    <template v-else>
      <component
        :is="viewComponent"
        :data="computedData"
        :code="code"
        :loading="loading"
        :partyId="partyId"
        :orgSlug="orgSlug"
        :queueInfo="queueInfo"
        @refresh="getParty"
        @changeParty="selectedParty = $event"
      />
    </template>
    <fb-pixel
      v-if="data?.party?.fbPixelId"
      :fbPixelId="data?.party?.fbPixelId"
    />
    <chatwoot-widget
      v-if="!!data"
      :data="{
        name: data?.party?.name,
        url: $route.path,
      }"
    />
  </div>
</template>

<script>
const { VUE_APP_PLATFORM_ID } = process.env;
import SHOP from "@/services/shop";
import QUEUE from "@/services/queue";
import ACCOUNT from "@/services/account";

import { settings } from "@/themeConfig.js";

import PartyMobile from "./PartyMobile.vue";
import PartyDesktop from "./PartyDesktop.vue";
import PartyFreeDesktop from "./PartyFreeDesktop.vue";
import PartyFreeMobile from "./PartyFreeMobile.vue";
import ErrorHandler from "./ErrorHandler.vue";
import FbPixel from "@/components/global/FbPixel.vue";
import ChatwootWidget from "@/components/global/ChatwootWidget.vue";
export default {
  metaInfo() {
    return {
      title: this.titlePage,
      script: this.partySchema
        ? [
            {
              type: "application/ld+json",
              json: this.partySchema,
            },
          ]
        : [],
    };
  },
  components: {
    PartyMobile,
    PartyDesktop,
    PartyFreeDesktop,
    PartyFreeMobile,
    ErrorHandler,
    FbPixel,
    ChatwootWidget,
  },
  data: () => ({
    code: null,
    seller: null,
    innerLoading: false,
    loading: true,
    error: null,
    data: null,
    forceSmall: false,
    selectedParty: null,
    coupon: null,
    queueInfo: null,
    queuePingInterval: null,
    concession: null,
  }),
  computed: {
    singlePartyConfig() {
      return settings?.singleParty || null;
    },
    partyId() {
      return this.$route?.params?.partyId || this.singlePartyConfig?.partyId;
    },
    orgSlug() {
      return (
        this.$route?.params?.orgSlug || this.singlePartyConfig?.organizationSlug
      );
    },
    viewComponent() {
      const isMobile = this.$vuetify.breakpoint.xsOnly;
      const forceSmall = this.forceSmall;

      if (this.data?.party?.isFree)
        return isMobile || forceSmall ? "PartyFreeMobile" : "PartyFreeDesktop";

      return isMobile || forceSmall ? "PartyMobile" : "PartyDesktop";
    },

    computedData() {
      var data = {
        ...this.data,
        concession: this.concession,
      };

      if (!this.selectedParty) return data;
      const party = this.data.party?.ChildParties.find(
        (p) => p.id == this.selectedParty
      );
      if (!party) return data;

      return {
        ...data,
        concession: this.concession,
        baseParty: data.party,
        party: {
          ...data.party,
          name: party.name,
          subtitle: party.subtitle,
          cover: party.cover,
          date: party.date,
          endDate: party.endDate,
          description: party.description,
          spotifyId: party.spotifyId,
          youtubeId: party.youtubeId,
        },
      };
    },
    ticketPrice() {
      if (!this.data?.ticketGroups) return null;
      const in_stock = this.data.ticketGroups.some((tg) =>
        tg.ticketBlocks.some((t) => t.status == "available")
      );
      if (!in_stock) return false;
      return this.data.ticketGroups.reduce((acc, tg) => {
        const price = tg.ticketBlocks.reduce((acc, t) => {
          if (t.status == "available" && t.price < acc) return t.price;
          return acc;
        }, Infinity);
        if (price < acc) return price;
        return acc;
      }, Infinity);
    },
    titlePage() {
      return this.data?.party?.name || "Shop - Evento";
    },
    partySchema() {
      if (!this.data?.party) return null;
      return {
        "@context": "http://schema.org",
        "@type": "Event",
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        name: this.data.party.name,
        description: this.data.party.description,
        startDate: this.data.party.date,
        endDate: this.data.party.endDate,
        location: {
          "@type": "Place",
          name: this.data.party.Address?.name,
          address: {
            "@type": "PostalAddress",
            streetAddress: this.data?.party?.Address?.address,
            addressLocality: this.data?.party?.Address?.city,
            addressRegion: this.data?.party?.Address?.state,
            postalCode: this.data?.party?.Address?.zipCode,
            addressCountry: this.data?.party?.Address?.country,
          },
        },
        image: this.data?.party?.cover,
        offers: {
          "@type": "Offer",
          url: window.location.href,
          price: this.ticketPrice,
          priceCurrency: "BRL",
          availability:
            "http://schema.org/" + (this.ticketPrice ? "InStock" : "SoldOut"),
        },
        organizer: {
          "@type": "Organization",
          name: this.data?.organization?.name,
          url:
            window.location.origin + "/shop/" + this.data?.organization?.slug,
        },
      };
    },
  },
  methods: {
    applyCoupon(coupon, loading = false) {
      this.coupon = coupon;
      this.getParty(loading);
    },
    async getParty(loading = true) {
      try {
        if (this.innerLoading) return;
        this.innerLoading = true;
        this.loading = loading;

        let headers = {};
        if (this.queueInfo?.token) {
          headers["x-queue-token"] = this.queueInfo?.token;
        }

        let response = await SHOP.party(
          this.orgSlug,
          this.partyId,
          {
            code: this.code,
            seller: this.seller,
            coupon: this.coupon,
          },
          headers
        );

        if (response?.queue) {
          const queueInfo = {
            ...response.queue,
            party: response.party,
          };
          window.sessionStorage.setItem("queue", JSON.stringify(queueInfo));
          this.queueInfo = response.queue;

          this.$posthog.capture("queue", {
            status: queueInfo.status,
            party: response?.party?.id,
            id: queueInfo.id,
          });

          if (queueInfo.status === "waiting") {
            this.$router.replace({
              name: "queue.party",
              params: {
                partyId: this.partyId,
                orgSlug: this.orgSlug,
              },
            });
            return;
          } else if (queueInfo.status === "processing") {
            this.checkQueue();
            this.queuePingInterval = setInterval(this.checkQueue, 20000);
          }
        }

        const org = response?.organization;
        if (org?.Platform && org.Platform?.id !== VUE_APP_PLATFORM_ID) {
          const uri = `//${org.Platform.domain}/shop/${org.slug}/${this.partyId}`;
          window.location.href = uri;
          return;
        }

        const hasConcessionTicket = response.ticketGroups.some((tg) =>
          tg.ticketBlocks.some(
            (t) => t.concessionTicket && t.concessionPreVerify
          )
        );
        if (hasConcessionTicket) this.verifyUserConcession();

        this.data = response;

        this.loading = false;
      } catch (error) {
        console.error(error);
        this.error = error;
        this.loading = false;
        this.$posthog.capture("error_party_load", {
          error: error,
          partyId: this.partyId,
          orgSlug: this.orgSlug,
        });
      } finally {
        this.innerLoading = false;
      }
    },
    async verifyUserConcession() {
      try {
        const data = await ACCOUNT.concession.get();

        if (data.concession) {
          var isValid = data.concession.status === "active";
          if (data.expireAt && moment(data.expireAt).isBefore(moment()))
            isValid = false;

          this.concession = {
            ...data.concession,
            isValid,
          };
        }
      } catch (error) {
        console.error(error);
      }
    },
    async checkQueue() {
      try {
        await QUEUE.evolution(this.queueInfo.token);
      } catch (error) {
        console.error(error);
      }
    },
    manageCode() {
      try {
        var localStorageItem = JSON.parse(
          window.localStorage.getItem("shop-code") || "{}"
        );

        if (this.$route.hash) {
          const code = this.$route.hash.replace("#", "");
          const isValid =
            /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
              code
            );
          if (!isValid) return;

          this.code = code;

          window.localStorage.setItem(
            "shop-code",
            JSON.stringify({
              ...localStorageItem,
              [this.partyId]: this.code,
            })
          );

          this.$router.push({ hash: null, query: this.$route.query });
        } else {
          this.code = localStorageItem[this.partyId] || null;
        }
      } catch (e) {
        console.log(e);
        window.localStorage.removeItem("shop-code");
      }
    },
    manageSeller() {
      try {
        var localStorageItem = JSON.parse(
          window.localStorage.getItem("shop-seller") || "{}"
        );

        if (this.$route?.query?.seller) {
          localStorageItem = localStorageItem || {};
          this.seller = this.$route.query.seller;

          window.localStorage.setItem(
            "shop-seller",
            JSON.stringify({
              ...localStorageItem,
              [this.partyId]: this.seller,
            })
          );
        } else {
          this.seller = localStorageItem[this.partyId] || null;
        }
      } catch (e) {
        console.log(e);
        window.localStorage.removeItem("shop-seller");
      }
    },
    manageQueue() {
      try {
        const queueInfo = JSON.parse(
          window.sessionStorage.getItem("queue") || "{}"
        );
        if (!queueInfo.status) return false;

        if ([queueInfo.party.id, queueInfo.party.slug].includes(this.partyId)) {
          if (queueInfo.status !== "waiting") {
            this.queueInfo = queueInfo;
            return false;
          }
          this.$router.replace({
            name: "queue.party",
            params: {
              partyId: this.partyId,
              orgSlug: this.orgSlug,
            },
          });
          return true;
        }

        return false;
      } catch (e) {
        console.log(e);
        window.sessionStorage.removeItem("queue");
      }
    },
    removeSeller() {
      var localStorageItem = JSON.parse(
        window.localStorage.getItem("shop-seller") || "{}"
      );

      delete localStorageItem[this.partyId];

      window.localStorage.setItem(
        "shop-seller",
        JSON.stringify(localStorageItem)
      );

      this.seller = null;
      this.getParty(true);
    },
    onUnload(event) {
      console.log("onUnload", event);
    },
  },
  watch: {
    partyId: {
      handler(val, oldVal) {
        if (!oldVal) return;
        if (this.selectedParty) return;
        this.data = null;
        this.getParty();
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.$vuetify.breakpoint.xsOnly) this.forceSmall = true;

    if (this.manageQueue()) return;
    this.manageSeller();
    this.manageCode();

    var coupon = this.$route?.query?.cupom;
    if (!coupon && !this.$route?.params?.partyId)
      coupon = this.singlePartyConfig?.coupon;
    if (coupon) this.applyCoupon(coupon, true);
    else this.getParty(true);

    this.$root.$on("refresh-party", () => this.getParty(true));
    this.$root.$on("remove-seller", () => this.removeSeller());
    this.$root.$on("applyCoupon", (coupon) => this.applyCoupon(coupon));
  },
  beforeDestroy() {
    if (this.queuePingInterval) clearInterval(this.queuePingInterval);
  },
};
</script>

<style></style>
